import React from 'react';

import { fetchLivePages, type LivePages } from '../../landing-page-api';

/** The live pages read, stamped with the brand it was made for (so a brand switch never shows the previous one's). */
export type LiveRead =
  | { tenantId: number; status: 'ready'; data: LivePages }
  | { tenantId: number; status: 'error'; httpStatus: number };

export interface UseLivePages {
  /** This brand's read (null until its first answer, and for another brand's). */
  current: LiveRead | null;
  /** The brand's live pages and landing hosts (null until read, or when the first read failed). */
  live: LivePages | null;
  /** The first read for this brand failed. */
  failed: boolean;
  /** Read again (a publish or an unpublish changed them). */
  reload: () => void;
}

/**
 * The brand's pages live on its landing domain (and its landing hosts), shared by the Landing Pages library and the
 * in-place asset overlay. A re-read that fails keeps the brand's last good answer; only a first read that fails is the
 * error state.
 */
export const useLivePages = (tenantId: number | undefined): UseLivePages => {
  const [read, setRead] = React.useState<LiveRead | null>(null);
  const [reloads, setReloads] = React.useState(0);
  React.useEffect(() => {
    // Syncs with spark-creatives: the brand's live pages, read again on each reload.
    if (!tenantId) return undefined;
    const controller = new AbortController();
    void fetchLivePages(tenantId, controller.signal).then((res) => {
      if (controller.signal.aborted) return;
      if (res.ok) setRead({ tenantId, status: 'ready', data: res.data });
      else if (!res.aborted)
        setRead((held) =>
          held?.tenantId === tenantId && held.status === 'ready'
            ? held
            : { tenantId, status: 'error', httpStatus: res.status },
        );
    });
    return () => controller.abort();
  }, [tenantId, reloads]);
  const reload = React.useCallback((): void => setReloads((n) => n + 1), []);
  const current = read && read.tenantId === tenantId ? read : null;
  return {
    current,
    live: current?.status === 'ready' ? current.data : null,
    failed: current?.status === 'error',
    reload,
  };
};
